import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AddEditProps, Group } from '../models/table-models';
import { Controller, useForm } from 'react-hook-form';
import CommonInputText from '../components/CommonInputText';
import {
    useAddGroup,
    useGetAllMainGroups,
    useGetAllTimeZones,
    useUpdateGroup,
} from '../utils/groups-hooks';
import React, { useState } from 'react';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import StepWizard from 'react-step-wizard';
import { GeoCodingMap } from '../components/Map/GeoCodingMap';
import { MapPosition } from '../models/dashboard-models';
import Select from 'react-select';
import { stringsToOptions, stringToOption } from '../components/FormMultiSelect';
import { CustomStepWizardNav } from '../components/CustomStepWizardNav';
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";

export const AddEditGroup = ({
    itemToEdit,
    setShowModal,
    refetchEntitiesHook,
}: AddEditProps<Group>) => {
    const [position, setPosition] = useState<MapPosition|undefined>(undefined);
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const [getTimezones] = useAsyncResource(useGetAllTimeZones, []);
    const timezones = getTimezones();
    const allLanguages = ['BG', 'EN'];
    const { register, handleSubmit, formState, control } = useForm<Group>({
        mode: 'all',
        defaultValues: { ...itemToEdit },
    });

    const useSubmitHandlerAdd = async (formValue: Group) => {
        formValue.defaultView = position;
        await useAddGroup(formValue);
        refetchEntitiesHook();
        resourceCache(useGetAllMainGroups).clear();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('groupCreated')]}/>, toastOptionsTopRight);
    };

    const useSubmitHandlerEdit = async (formValue: Group) => {
        formValue.defaultView = position;
        await useUpdateGroup(formValue, itemToEdit?.id);
        refetchEntitiesHook();
        setShowModal(false);
    };

    return (
        <StepWizard
            className="step-wizard d-flex flex-wrap w-100"
            nav={<CustomStepWizardNav steps={['group', 'mapPosition']} />}
            transitions={{
                enterRight: '',
                enterLeft: '',
                exitRight: '',
                exitLeft: '',
            }}
        >
            <Form
                id="form"
                className="d-flex flex-column align-items-center p-4  w-100"
                noValidate
                onSubmit={
                    itemToEdit
                        ? handleSubmit(useSubmitHandlerEdit)
                        : handleSubmit(useSubmitHandlerAdd)
                }
            >
                {position==undefined && (
                    <p className={`input-error`}>{'*'+t('choosePosition')}</p>
                )}
                <Form.Group
                    className=" d-flex flex-column mb-2 align-items-start w-100 "
                    controlId="mainGroup"
                >
                    <CommonInputText
                        label={t('mainGroup')}
                        defaultValue={itemToEdit?.mainGroup}
                        labelTextColor="text-black"
                        type="text"
                        fieldError={formState.errors.mainGroup}
                        hookRegister={register('mainGroup', {
                            required: {
                                value: true,
                                message: t('mainGroup'),
                            },
                        })}
                    />
                </Form.Group>

                <Form.Group
                    className=" d-flex flex-column mb-2 align-items-start w-100 "
                    controlId="subGroup"
                >
                    <CommonInputText
                        label={t('subGroup')}
                        defaultValue={itemToEdit?.subGroup}
                        labelTextColor="text-black"
                        type="text"
                        fieldError={formState.errors.subGroup}
                        hookRegister={register('subGroup', {
                            required: {
                                value: true,
                                message: t('subGroup'),
                            },
                        })}
                    />
                </Form.Group>

                <Form.Group
                    className=" d-flex flex-column mb-2 align-items-start w-100 "
                    controlId="timeZone"
                >
                    <Form.Label>{t('timeZone')}</Form.Label>
                    <Controller
                        control={control}
                        name="timezone"
                        rules={{ required: true }}
                        defaultValue={itemToEdit?.timezone}
                        render={({ field }) => (
                            <>
                                <Select
                                    className="w-100"
                                    defaultValue={stringToOption(itemToEdit?.timezone)}
                                    onChange={(newValue) => field.onChange(newValue?.value)}
                                    options={stringsToOptions(timezones)}
                                    getOptionLabel={({ name }) => name}
                                    getOptionValue={({ value }) => value}
                                />
                                {formState.errors.timezone && (
                                    <p className={`input-error`}>{'*'+t('choose')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>

                <Form.Group
                    className=" d-flex flex-column mb-2 align-items-start w-100 "
                    controlId="language"
                >
                    <Form.Label>{t('language')}</Form.Label>
                    <Controller
                        control={control}
                        name="language"
                        rules={{ required: true }}
                        defaultValue={itemToEdit?.language}
                        render={({ field }) => (
                            <>
                                <Select
                                    className="w-100"
                                    defaultValue={stringToOption(itemToEdit?.language)}
                                    onChange={(newValue) => field.onChange(newValue?.value)}
                                    options={stringsToOptions(allLanguages)}
                                    getOptionLabel={({ name }) => name}
                                    getOptionValue={({ value }) => value}
                                />
                                {formState.errors.language && (
                                    <p className={`input-error`}>{'*'+t('choose')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
            </Form>
            <div className="map-modal-container">
                <GeoCodingMap
                    {...{ setPosition }}
                    defaultPosition={itemToEdit?.defaultView}
                ></GeoCodingMap>
            </div>
        </StepWizard>
    );
};
